"use client";

import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { getAccessToken } from "@/lib/auth/client";
import type { GuestPassCard, GuestPassSummaryResponse } from "@/app/components/types";

interface GuestPassContextValue {
  passes: GuestPassCard[];
  summary: GuestPassSummaryResponse["summary"];
  loading: boolean;
  reload: () => void;
}

const GuestPassContext = createContext<GuestPassContextValue>({
  passes: [],
  summary: { totalAllocated: 0, totalIssued: 0, totalRemaining: 0 },
  loading: true,
  reload: () => {},
});

export function GuestPassProvider({ children }: { children: ReactNode }) {
  const [passes, setPasses] = useState<GuestPassCard[]>([]);
  const [summary, setSummary] = useState<GuestPassSummaryResponse["summary"]>({
    totalAllocated: 0,
    totalIssued: 0,
    totalRemaining: 0,
  });
  const [loading, setLoading] = useState(true);
  const inFlight = useRef(false);

  const load = useCallback(async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const token = getAccessToken();
      const res = await fetch("/api/guest-passes", {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error(`${res.status}`);
      const json = await res.json();
      setPasses(json.data?.passes ?? []);
      setSummary(json.data?.summary ?? { totalAllocated: 0, totalIssued: 0, totalRemaining: 0 });
    } catch {
      setPasses([]);
      setSummary({ totalAllocated: 0, totalIssued: 0, totalRemaining: 0 });
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <GuestPassContext.Provider value={{ passes, summary, loading, reload: load }}>
      {children}
    </GuestPassContext.Provider>
  );
}

export function useGuestPasses() {
  return useContext(GuestPassContext);
}
